// Shared constants + formatters for the lab app. Mirrors the lifecycle and
// role gates in lab-app-appointments-api.md so the UI only offers moves the
// server will accept; the server still enforces everything.

export const STATUS = {
  REQUESTED: "REQUESTED",
  CONFIRMED: "CONFIRMED",
  CHECKED_IN: "CHECKED_IN",
  SAMPLE_COLLECTED: "SAMPLE_COLLECTED",
  IN_PROCESS: "IN_PROCESS",
  REPORT_READY: "REPORT_READY",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  NO_SHOW: "NO_SHOW",
};

export const STATUS_ORDER = [
  "REQUESTED", "CONFIRMED", "CHECKED_IN", "SAMPLE_COLLECTED", "IN_PROCESS",
  "REPORT_READY", "COMPLETED", "CANCELLED", "NO_SHOW",
];

export const STATUS_LABEL = {
  REQUESTED: "Requested",
  CONFIRMED: "Confirmed",
  CHECKED_IN: "Checked in",
  SAMPLE_COLLECTED: "Sample collected",
  IN_PROCESS: "In process",
  REPORT_READY: "Report ready",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  NO_SHOW: "No show",
};

export const TERMINAL = new Set(["COMPLETED", "CANCELLED", "NO_SHOW"]);

// Board columns for the Today screen
export const BOARD_COLUMNS = [
  { id: "upcoming", title: "Upcoming", statuses: ["REQUESTED", "CONFIRMED"] },
  { id: "inlab", title: "In lab", statuses: ["CHECKED_IN", "SAMPLE_COLLECTED"] },
  { id: "processing", title: "Processing", statuses: ["IN_PROCESS"] },
  { id: "ready", title: "Report ready", statuses: ["REPORT_READY"] },
  { id: "done", title: "Completed", statuses: ["COMPLETED"] },
];

export const PAYMENT_METHODS = ["Cash", "Card", "UPI", "Insurance", "Other"];
export const INVOICE_STATUSES = ["Pending", "Partially Paid", "Paid", "Cancelled"];
export const SKU_KINDS = ["test", "panel", "scan", "package", "service"];
export const SERVICE_TYPES = ["pathology", "radiology"];
// Bookable modalities (radiology doc §1). "CAT" exists on legacy records only.
export const MODALITIES = ["Xray", "CT", "MRI", "Sonography", "Doppler", "Echo", "Mammography", "DEXA", "OPG", "PET-CT", "NuclearMedicine", "Fluoroscopy", "IVP"];
export const MODALITY_LABEL = { "PET-CT": "PET-CT", NuclearMedicine: "Nuclear medicine", Xray: "X-ray", DEXA: "DEXA", OPG: "OPG", IVP: "IVP", CAT: "CAT (legacy)" };
export const modalityLabel = (m) => MODALITY_LABEL[m] || m;
export const TEST_CLASSIFICATIONS = ["Normal", "Abnormal", "Warning", "Undefined"];
export const GENDERS = ["Male", "Female", "Other"];

// Role gates for actions (§1 of the appointments doc). `can` from useAuth
// applies the owner > admin > (signatory | staff | front_desk) hierarchy.
export const GATE = {
  catalogueWrite: ["admin"],
  catalogueDelete: ["owner"],
  book: ["front_desk", "staff"],
  statusChange: ["front_desk", "staff", "signatory"],
  reportReady: ["signatory"],
  cancelAfterCheckIn: ["admin"],
  attachReports: ["staff", "signatory"],
  deleteAppointment: ["owner"],
  invoiceWrite: ["front_desk", "staff"],
  billingAdmin: ["admin"],
  deleteInvoice: ["owner"],
};

// Which status moves are available from the current one, with the role that
// may take them. Cancel gates tighten after check-in.
export function nextMoves(appt) {
  const s = appt.status;
  const pathology = appt.serviceType === "pathology";
  const home = appt.collectionType === "home";
  const moves = [];
  const add = (status, roles = GATE.statusChange, extra = {}) => moves.push({ status, roles, ...extra });

  switch (s) {
    case "REQUESTED":
      add("CONFIRMED");
      add("CANCELLED", GATE.statusChange, { reason: true });
      break;
    case "CONFIRMED":
      if (home) add("SAMPLE_COLLECTED");
      else add("CHECKED_IN");
      add("NO_SHOW");
      add("CANCELLED", GATE.statusChange, { reason: true });
      break;
    case "CHECKED_IN":
      if (pathology) add("SAMPLE_COLLECTED");
      else add("IN_PROCESS");
      add("CANCELLED", GATE.cancelAfterCheckIn, { reason: true });
      break;
    case "SAMPLE_COLLECTED":
      add("IN_PROCESS");
      add("CANCELLED", GATE.cancelAfterCheckIn, { reason: true });
      break;
    case "IN_PROCESS":
      add("REPORT_READY", GATE.reportReady);
      add("CANCELLED", GATE.cancelAfterCheckIn, { reason: true });
      break;
    case "REPORT_READY":
      add("COMPLETED");
      break;
    default:
      break;
  }
  return moves;
}

// ── Formatting ───────────────────────────────────────────────────────────────
const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 });
export const money = (n) => inr.format(Number(n) || 0);

export const pad2 = (n) => String(n).padStart(2, "0");
export const toDateInput = (d = new Date()) => {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
};
export const toTimeInput = (d) => {
  const x = new Date(d);
  return `${pad2(x.getHours())}:${pad2(x.getMinutes())}`;
};
export const todayStr = () => toDateInput(new Date());
export const addDays = (dateStr, n) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return toDateInput(d);
};

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
export const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";
export const fmtDateTime = (d) => (d ? `${fmtDate(d)}, ${fmtTime(d)}` : "—");

export const fullName = (name) =>
  [name?.firstName, name?.middleName, name?.lastName].filter(Boolean).join(" ") || "—";

export const phoneOf = (contact) => contact?.phoneNumber?.phoneNumber || contact?.phoneNumber || "";

export const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "");

export const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

// Client-side estimate of a booking's amount using the SKU price/tax and
// per-line percent discount; the server recomputes on save.
export function estimateAmount(lines) {
  let subtotal = 0, discount = 0, tax = 0;
  for (const l of lines) {
    const qty = Number(l.qty) || 1;
    const price = Number(l.unitPrice) || 0;
    const sub = price * qty;
    const disc = sub * ((Number(l.discount) || 0) / 100);
    const taxPct = (l.tax?.cgst || 0) + (l.tax?.sgst || 0) + (l.tax?.igst || 0);
    const tx = (sub - disc) * (taxPct / 100);
    subtotal += sub; discount += disc; tax += tx;
  }
  const r = (n) => Math.round(n * 100) / 100;
  return { subtotal: r(subtotal), discount: r(discount), tax: r(tax), total: r(subtotal - discount + tax) };
}

// ── Results & templates ──────────────────────────────────────────────────────
export const RESULT_STATUS_ORDER = ["PENDING", "ENTERED", "VERIFIED", "PUBLISHED", "CANCELLED"];
export const RESULT_STATUS_LABEL = {
  PENDING: "Pending", ENTERED: "Entered", VERIFIED: "Verified", PUBLISHED: "Published", CANCELLED: "Cancelled",
};
export const FLAG_LABEL = { "": "", N: "Normal", L: "Low", H: "High", LL: "Critical low", HH: "Critical high", A: "Abnormal" };
export const RESULT_TYPES = ["numeric", "text", "select", "calculated"];
export const RANGE_SEX = ["any", "Male", "Female"];

Object.assign(GATE, {
  templatesWrite: ["admin"],
  templatesDelete: ["owner"],
  enterValues: ["staff", "signatory"],
  verifyResults: ["signatory"],
  cancelResult: ["admin"],
});

export const daysToYears = (d) => (d == null || d === "" ? "" : Math.round((Number(d) / 365) * 100) / 100);
export const yearsToDays = (y) => (y === "" || y == null ? null : Math.round(Number(y) * 365));

// "13 - 17 g/dL" / "< 5" / "Negative" for a resolved reference range
export const rangeText = (rr, unit = "") => {
  if (!rr) return "—";
  if (rr.text) return rr.text;
  const u = unit ? ` ${unit}` : "";
  if (rr.low != null && rr.high != null) return `${rr.low} – ${rr.high}${u}`;
  if (rr.low != null) return `> ${rr.low}${u}`;
  if (rr.high != null) return `< ${rr.high}${u}`;
  return "—";
};

// ── Members ──────────────────────────────────────────────────────────────────
export const ROLES = ["owner", "admin", "signatory", "staff", "radiographer", "front_desk"];
export const ROLE_RANK = { owner: 3, admin: 2, signatory: 1, staff: 1, radiographer: 1, front_desk: 1 };
export const STAFF_TYPES = ["lab_technician", "pathologist", "radiologist", "radiographer", "phlebotomist", "sonographer", "front_desk", "general"];
export const EMPLOYMENT_TYPES = ["full_time", "part_time", "contract", "visiting"];

// Rank rule (frontend guide §2.1): owner manages anyone; admin only the three lower roles
export const canManageRole = (myRole, targetRole) =>
  myRole === "owner" || (myRole === "admin" && (ROLE_RANK[targetRole] ?? 0) < ROLE_RANK.admin);
export const grantableRoles = (myRole) =>
  myRole === "owner" ? ROLES : myRole === "admin" ? ["signatory", "staff", "radiographer", "front_desk"] : [];

Object.assign(GATE, {
  manageMembers: ["admin"],
  branding: ["admin"],
  attachReport: ["signatory"],
});

export const LAYOUTS = ["branded", "letterhead"];

// File → { data: <dataURI>, contentType } for the image upload endpoints
export const fileToImage = (file, maxBytes) =>
  new Promise((resolve, reject) => {
    if (!file) return reject(new Error("Choose a file"));
    if (!["image/png", "image/jpeg"].includes(file.type)) return reject(new Error("PNG or JPEG only"));
    if (maxBytes && file.size > maxBytes) return reject(new Error(`File must be under ${Math.round(maxBytes / 1024 / 1024)} MB`));
    const reader = new FileReader();
    reader.onload = () => resolve({ data: reader.result, contentType: file.type });
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });

// Opens a PDF route in a new tab (navigation requests carry the session cookie)
export const openPdf = (url) => window.open(url, "_blank", "noopener");

// ── Specimens ────────────────────────────────────────────────────────────────
export const TUBE_NAMES = { E: "EDTA", S: "Serum", F: "Fluoride", C: "Citrate", H: "Heparin", P: "Plasma", U: "Urine", ST: "Stool", SW: "Swab", B: "Blood" };
export const tubeName = (code) => TUBE_NAMES[String(code || "").replace(/\d+$/, "")] || code;
export const REJECT_REASONS = ["haemolysed", "clotted", "insufficient volume", "wrong tube", "unlabelled", "leaked"];

Object.assign(GATE, {
  collectSpecimen: ["front_desk", "staff"],
  rejectSpecimen: ["staff", "signatory"],
  sendReport: ["front_desk", "staff", "signatory"],
  notifyPatient: ["front_desk", "staff"],
});

// Mask a phone for display: 9812345678 → 98xxxxxx78
export const maskPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `${d.slice(0, 2)}xxxxxx${d.slice(-2)}` : d || "—";
};

// One-line summary of a report delivery attempt (publish response / history)
export const deliveryLine = (d) => {
  if (!d) return "";
  const what = d.partial ? `Partial report (${d.publishedCount} of ${d.totalCount} tests)` : "Report";
  return d.status === "sent" ? `${what} sent to ${maskPhone(d.to)}` : `${what} could not be sent${d.error ? `: ${d.error}` : ""}`;
};

// Normalise a scanned/typed sticker code the way the server does
export const normalizeSticker = (raw) => String(raw || "").replace(/\s+/g, "").toUpperCase();

// A scanned value is either an accession number (LAB81264-260920-0042, or a
// day prefix LAB81264-260920) or a pre-printed sticker code (DL000123).
export const parseScan = (raw) => {
  const v = normalizeSticker(raw);
  if (/^[A-Z0-9]+-\d{6}(-\d{4})?$/.test(v)) return { sampleId: v, label: null };
  return { sampleId: null, label: v };
};

// Derive a roll's registration from what's printed on its first and last sticker
export const parseRoll = (first, last) => {
  const a = normalizeSticker(first), b = normalizeSticker(last);
  const m1 = a.match(/^([A-Z0-9]{1,6}?)(\d{3,10})$/), m2 = b.match(/^([A-Z0-9]{1,6}?)(\d{3,10})$/);
  if (!m1 || !m2) return { error: "Codes must be a short prefix followed by 3–10 digits, e.g. DL000001" };
  if (m1[1] !== m2[1]) return { error: `Prefixes differ (${m1[1]} vs ${m2[1]})` };
  if (m1[2].length !== m2[2].length) return { error: "First and last sticker must have the same number of digits" };
  const rangeStart = parseInt(m1[2], 10), rangeEnd = parseInt(m2[2], 10);
  if (rangeEnd < rangeStart) return { error: "Last sticker is before the first" };
  return { prefix: m1[1], numberWidth: m1[2].length, rangeStart, rangeEnd };
};
export const stickerCode = (prefix, width, n) => `${prefix}${String(n).padStart(width, "0")}`;

export const toLocalDateTimeInput = (d = new Date()) => {
  const x = d instanceof Date ? d : new Date(d);
  return `${toDateInput(x)}T${pad2(x.getHours())}:${pad2(x.getMinutes())}`;
};

// One-line summary of a booking notification (confirmation / reminder)
export const notificationLine = (n) => {
  if (!n) return "";
  const what = n.type === "Reminder" ? "Reminder" : "Confirmation";
  return n.status === "sent" ? `${what} sent to ${maskPhone(n.to)}` : `${what} could not be sent${n.error ? `: ${n.error}` : ""}`;
};

// Lab.name is { fullName, aliasName } on the record but a plain string on
// denormalised fields like LabAppointment.labName — accept either.
export const labName = (name) => (typeof name === "string" ? name : name?.fullName || name?.aliasName || "");

// ── Sticker printing (specimens guide §2.1) ──────────────────────────────────
export const STICKER_DEFAULTS = {
  layout: "sheet", pageSize: "A4", widthMm: 50, heightMm: 25, marginMm: 8, gapMm: 2,
  showLogo: false, showName: true, showText: true, startAt: 0, labelText: "",
};
// Common blank-label stock sizes
export const STICKER_SIZES = [
  { label: "50 × 25 mm", widthMm: 50, heightMm: 25 },
  { label: "38 × 21 mm", widthMm: 38, heightMm: 21 },
  { label: "25 × 15 mm", widthMm: 25, heightMm: 15 },
];
export const STICKER_LIMITS = { widthMm: [20, 210], heightMm: [10, 297], marginMm: [0, 40], gapMm: [0, 20] };
export const STICKER_MAX = 2000;

// ── Radiology (radiology doc §3) ─────────────────────────────────────────────
export const RAD_STATUS_ORDER = ["PENDING", "ACQUIRED", "DRAFT", "VERIFIED", "PUBLISHED", "CANCELLED"];
export const RAD_STATUS_LABEL = {
  PENDING: "Not scanned", ACQUIRED: "Scan done", DRAFT: "Draft report",
  VERIFIED: "Verified", PUBLISHED: "Published", CANCELLED: "Cancelled",
};
export const LATERALITY = ["", "Left", "Right", "Bilateral", "NA"];
export const CONTRAST_ROUTES = ["IV", "Oral", "Rectal", "Intraarticular", "Other"];
export const REPORT_SECTIONS = [
  { key: "indication", label: "Indication", rows: 2 },
  { key: "technique", label: "Technique", rows: 3 },
  { key: "comparison", label: "Comparison", rows: 2 },
  { key: "findings", label: "Findings", rows: 10, required: true },
  { key: "impression", label: "Impression", rows: 4, required: true },
  { key: "recommendation", label: "Recommendation", rows: 3 },
];
export const MAX_KEY_IMAGES = 6;

// Images are read in the OHIF viewer (separate app, pointed at the gateway's
// /dicom-web). Its longitudinal mode opens a study at /viewer?StudyInstanceUIDs=
export const OHIF_URL = (process.env.REACT_APP_OHIF_URL || "https://viewer.cohesivehealth.in").replace(/\/$/, "");
export const ohifStudyUrl = (studyInstanceUID) =>
  OHIF_URL && studyInstanceUID ? `${OHIF_URL}/viewer?StudyInstanceUIDs=${encodeURIComponent(studyInstanceUID)}` : null;
export const DICOM_MAX_BYTES = 200 * 1024 * 1024;   // gateway buffers the body

Object.assign(GATE, {
  acquireScan: ["radiographer", "staff"],   // runs the scanner, uploads images
  reportStudy: ["signatory"],               // writes, verifies, publishes, addenda
  radTemplatesWrite: ["admin"],
  cancelStudy: ["admin"],
});

export const humanSize = (bytes) => {
  if (!bytes) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), u.length - 1);
  return `${Math.round((bytes / 1024 ** i) * 10) / 10} ${u[i]}`;
};
